import { useState } from 'react';
import { useI18n } from './i18n';
import { normalizePickupPhone, readPickupProfile, savePickupProfile, type SavedPickupDetails } from './pickupProfile';

function initialDetails(key: string, currentOrderName = '') {
  const saved = readPickupProfile(key);
  return { key, name: saved?.name ?? currentOrderName, phone: saved?.phone ?? '', saved, error: '' };
}

export function usePickupDetails(key: string) {
  const [state, setState] = useState(() => initialDetails(key));
  // Switch saved profiles and clear the previous phone. Preserve a manually entered
  // name for the current cart, as the existing guest -> account flow does.
  if (state.key !== key) setState(initialDetails(key, state.saved ? '' : state.name));
  function edit(field: 'name' | 'phone', value: string) { setState(s => ({ ...s, [field]: value, error: '' })); }
  function save() {
    if (!state.name.trim() || state.name.trim().length > 80) { setState(s => ({ ...s, error: 'name' })); return; }
    if (normalizePickupPhone(state.phone) === null) { setState(s => ({ ...s, error: 'phone' })); return; }
    try {
      const saved = savePickupProfile(key, state);
      setState(s => ({ ...s, name: saved.name, phone: saved.phone, saved, error: '' }));
    } catch { setState(s => ({ ...s, error: 'storage' })); }
  }
  function forget() {
    try {
      localStorage.removeItem(key);
      setState(s => ({ ...s, name: '', phone: '', saved: null as SavedPickupDetails | null, error: '' }));
    } catch { setState(s => ({ ...s, error: 'forgetError' })); }
  }
  const dirty = !!state.saved && (state.name !== state.saved.name || state.phone !== state.saved.phone);
  return { ...state, dirty, edit, save, forget };
}

export function PickupDetails({ details, busy }: { details: ReturnType<typeof usePickupDetails>; busy: boolean }) {
  const { t } = useI18n();
  return <div className="pickup-details">
    <label htmlFor="pickup">{t('cart.pickupName')}</label>
    <input id="pickup" autoComplete="name" disabled={busy} placeholder={t('cart.pickupPlaceholder')} value={details.name}
      maxLength={80} onChange={e => details.edit('name', e.target.value)} aria-invalid={details.error === 'name'}
      aria-describedby={details.error === 'name' ? 'pickup-details-error' : undefined} />
    <label htmlFor="pickup-phone">{t('pickup.phone')}</label>
    <input id="pickup-phone" type="tel" inputMode="tel" autoComplete="tel" disabled={busy} placeholder="08… / +62…"
      value={details.phone} maxLength={40} onChange={e => details.edit('phone', e.target.value)}
      aria-invalid={details.error === 'phone'} aria-describedby={`pickup-details-help${details.error === 'phone' ? ' pickup-details-error' : ''}`} />
    <p id="pickup-details-help" className="fine">{t('pickup.help')}</p>
    <div className="pickup-details-actions">
      <button type="button" className="secondary" disabled={busy || (!!details.saved && !details.dirty)} onClick={details.save}>
        {t(details.saved && !details.dirty ? 'pickup.savedButton' : 'pickup.save')}
      </button>
      {details.saved && <button type="button" className="secondary" disabled={busy} onClick={details.forget}>{t('pickup.forget')}</button>}
    </div>
    {details.error && <p id="pickup-details-error" className="error" role="alert">{t(`pickup.${details.error === 'phone' ? 'phoneError' : details.error}`)}</p>}
    {details.saved && !details.error && <p className="fine" role="status">{t(details.dirty ? 'pickup.unsaved' : 'pickup.saved')}</p>}
  </div>;
}
